import { InstantAnswerContext } from "@faire/help-center-self-serve/types";
import { Button } from "@faire/web--source/slate/Button";
import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import brandPortalOrdersbrandOrderToken from "@faire/web-api--source/routes/brand-portal/orders/brandOrderToken";
import React, { type JSX } from "react";

import { Bold } from "../common/Bold";

interface SplitOrderIAProps {
  context: InstantAnswerContext;
}

export const SplitOrderIA: React.FC<SplitOrderIAProps> = ({ context }) => {
  return (
    <Column>
      <Typography>
        <StrictLocalMsg
          defaultMessage="Splitting an order helps when you need to adjust specifics for some products but won't backorder items to a new date. To split an order: "
          description={{
            text: "Description for the instant answer about splitting an order",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="1. Visit the <OrderLinkFormatting>order details page</OrderLinkFormatting>."
          values={{
            OrderLinkFormatting: (chunks: JSX.Element) =>
              OrderLinkFormatting(
                chunks,
                context.brandOrder?.brand_order?.token
              ),
          }}
          description={{
            text: "Description for the instant answer about splitting an order",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="2. From there, select the <BoldFormatting>Split</BoldFormatting> option, usually found on the right-hand side."
          values={{
            BoldFormatting: (chunks: JSX.Element) => BoldFormatting(chunks),
          }}
          description={{
            text: "Description for the instant answer about splitting an order",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="3. You’ll see all products in the order along with their quantity. Here, specify the quantities you'd like to move to a new order."
          description={{
            text: "Description for the instant answer about splitting an order",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="4. After making your selections, select <BoldFormatting>Create new order</BoldFormatting>."
          values={{
            BoldFormatting: (chunks: JSX.Element) => BoldFormatting(chunks),
          }}
          description={{
            text: "Description for the instant answer about splitting an order",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="Both orders will have the same shipping address and expected ship date but the new order will have a unique brand order number."
          description={{
            text: "Description for the instant answer about splitting an order",
          }}
        />
      </Typography>
    </Column>
  );
};

const OrderLinkFormatting = (chunks: JSX.Element, brandOrderToken?: string) => {
  if (!brandOrderToken) {
    return <Bold>{chunks}</Bold>;
  } else {
    return (
      <Button
        variant="tertiary"
        linkTo={brandPortalOrdersbrandOrderToken(brandOrderToken)}
        target="_blank"
      >
        <Bold>{chunks}</Bold>
      </Button>
    );
  }
};

const BoldFormatting = (chunks: JSX.Element) => <Bold>{chunks}</Bold>;
